import { GRID_COLUMNS, clampColStart, getGridPlacement } from './defaults';

/** Distance max (en colonnes / lignes) pour aimanter. */
export const SNAP_THRESHOLD = 0.75;

function boxFromPlacement(p) {
  const colStart = Number(p.colStart) || 1;
  const colSpan = Number(p.colSpan) || 1;
  const row = Math.max(0, Number(p.row) || 0);
  const rowSpan = Math.max(1, Number(p.rowSpan) || 1);
  return {
    left: colStart,
    right: colStart + colSpan,
    centerX: colStart + colSpan / 2,
    top: row,
    bottom: row + rowSpan,
    centerY: row + rowSpan / 2,
    colSpan,
    rowSpan,
  };
}

/**
 * Collecte les lignes d’alignement (autres composants + centre de section).
 */
export function collectSnapTargets(sectionComponents, movingId, viewport = 'desktop') {
  const vertical = [
    { value: 1, kind: 'canvas-edge', label: 'bord gauche' },
    { value: GRID_COLUMNS + 1, kind: 'canvas-edge', label: 'bord droit' },
    { value: (GRID_COLUMNS + 2) / 2, kind: 'canvas-center', label: 'centre' },
  ];
  const horizontal = [{ value: 0, kind: 'canvas-edge', label: 'haut' }];

  for (const comp of sectionComponents || []) {
    if (!comp || comp.id === movingId) continue;
    const p = getGridPlacement(comp, viewport);
    const b = boxFromPlacement(p);
    vertical.push(
      { value: b.left, kind: 'edge', sourceId: comp.id },
      { value: b.right, kind: 'edge', sourceId: comp.id },
      { value: b.centerX, kind: 'center', sourceId: comp.id },
    );
    horizontal.push(
      { value: b.top, kind: 'edge', sourceId: comp.id },
      { value: b.bottom, kind: 'edge', sourceId: comp.id },
      { value: b.centerY, kind: 'center', sourceId: comp.id },
    );
  }

  return { vertical, horizontal };
}

function bestSnap(movingValues, targets, threshold) {
  let best = null;
  for (const mv of movingValues) {
    for (const t of targets) {
      const delta = t.value - mv.value;
      const abs = Math.abs(delta);
      if (abs > threshold) continue;
      if (!best || abs < best.abs) {
        best = {
          abs,
          delta,
          guideValue: t.value,
          kind: t.kind,
          movingEdge: mv.edge,
        };
      }
    }
  }
  return best;
}

/**
 * Ajuste le placement pour s’aligner / centrer, et retourne les guides actifs.
 */
export function snapPlacement(rawPlacement, targets, threshold = SNAP_THRESHOLD) {
  const box = boxFromPlacement(rawPlacement);
  let { colStart, colSpan, row, rowSpan } = {
    colStart: Number(rawPlacement.colStart) || 1,
    colSpan: Number(rawPlacement.colSpan) || 1,
    row: Math.max(0, Number(rawPlacement.row) || 0),
    rowSpan: Math.max(1, Number(rawPlacement.rowSpan) || 1),
  };

  const vSnap = bestSnap(
    [
      { edge: 'left', value: box.left },
      { edge: 'right', value: box.right },
      { edge: 'centerX', value: box.centerX },
    ],
    targets.vertical,
    threshold,
  );

  const hSnap = bestSnap(
    [
      { edge: 'top', value: box.top },
      { edge: 'bottom', value: box.bottom },
      { edge: 'centerY', value: box.centerY },
    ],
    targets.horizontal,
    threshold,
  );

  if (vSnap) {
    if (vSnap.movingEdge === 'left') {
      colStart = Math.round(vSnap.guideValue);
    } else if (vSnap.movingEdge === 'right') {
      colStart = Math.round(vSnap.guideValue - colSpan);
    } else {
      colStart = Math.round(vSnap.guideValue - colSpan / 2);
    }
    colStart = clampColStart(colStart, colSpan);
  }

  if (hSnap) {
    if (hSnap.movingEdge === 'top') {
      row = Math.max(0, Math.round(hSnap.guideValue));
    } else if (hSnap.movingEdge === 'bottom') {
      row = Math.max(0, Math.round(hSnap.guideValue - rowSpan));
    } else {
      row = Math.max(0, Math.round(hSnap.guideValue - rowSpan / 2));
    }
  }

  const snapped = { colStart, colSpan, row, rowSpan };
  const snappedBox = boxFromPlacement(snapped);

  const guides = [];
  if (vSnap) {
    // Recompute exact guide position after snap (aligned edge).
    let x = vSnap.guideValue;
    if (vSnap.movingEdge === 'left') x = snappedBox.left;
    else if (vSnap.movingEdge === 'right') x = snappedBox.right;
    else x = snappedBox.centerX;
    guides.push({
      axis: 'v',
      /** Position 0–1 relative à la grille (cols 1 … GRID_COLUMNS+1). */
      position: (x - 1) / GRID_COLUMNS,
      kind: vSnap.kind,
    });
  }
  if (hSnap) {
    let y = hSnap.guideValue;
    if (hSnap.movingEdge === 'top') y = snappedBox.top;
    else if (hSnap.movingEdge === 'bottom') y = snappedBox.bottom;
    else y = snappedBox.centerY;
    guides.push({
      axis: 'h',
      /** Ligne en unités de grille (0 = haut). */
      row: y,
      kind: hSnap.kind,
    });
  }

  return { placement: snapped, guides };
}

/**
 * Snap aussi les bords pendant un resize.
 */
export function snapResizePlacement(rawPlacement, handle, targets, threshold = SNAP_THRESHOLD) {
  const box = boxFromPlacement(rawPlacement);
  let { colStart, colSpan, row, rowSpan } = {
    colStart: Number(rawPlacement.colStart) || 1,
    colSpan: Number(rawPlacement.colSpan) || 1,
    row: Math.max(0, Number(rawPlacement.row) || 0),
    rowSpan: Math.max(1, Number(rawPlacement.rowSpan) || 1),
  };

  const guides = [];

  if (handle.includes('e')) {
    const snap = bestSnap([{ edge: 'right', value: box.right }], targets.vertical, threshold);
    if (snap) {
      const nextRight = Math.round(snap.guideValue);
      colSpan = Math.max(1, nextRight - colStart);
      colSpan = Math.min(GRID_COLUMNS - colStart + 1, colSpan);
      guides.push({
        axis: 'v',
        position: (nextRight - 1) / GRID_COLUMNS,
        kind: snap.kind,
      });
    }
  }

  if (handle.includes('s')) {
    const snap = bestSnap([{ edge: 'bottom', value: box.bottom }], targets.horizontal, threshold);
    if (snap) {
      const nextBottom = Math.round(snap.guideValue);
      rowSpan = Math.max(1, nextBottom - row);
      guides.push({
        axis: 'h',
        row: nextBottom,
        kind: snap.kind,
      });
    }
  }

  return { placement: { colStart, colSpan, row, rowSpan }, guides };
}
